import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  styled,
  InputAdornment,
  FormLabel,
} from "@mui/material";
import styles from "./CustomPointsRange.module.scss";
import KeyboardArrowDown from "../icon-components/KeyboardArrowDown";

// Styled TextField for consistent design
const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    paddingRight: "0",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#bdbdbd",
      borderWidth: "1px",
    },
  },
  "& .MuiInputAdornment-root": {
    margin: 0,
  },
});

interface PointsRangeComponentProps {
  min: number; // Minimum value
  max: number; // Maximum value
  step?: number; // Step value (default = 1)
  value?: number; // Initial value (default = min)
  onChange?: (value: number) => void; // Callback for value changes
  label?: string; // Label for the input field
  isRequired?: boolean; // Flag to indicate if the field is required
}

const PointsRangeComponent: React.FC<PointsRangeComponentProps> = ({
  min,
  max,
  step = 1,
  value = min,
  onChange,
  isRequired,
  label,
}) => {
  const [currentValue, setCurrentValue] = useState<number>(value);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleIncrement = () => {
    if (currentValue + step <= max) {
      const newValue = currentValue + step;
      setCurrentValue(newValue);
      onChange?.(newValue);
    }
  };

  const handleDecrement = () => {
    if (currentValue - step >= min) {
      const newValue = currentValue - step;
      setCurrentValue(newValue);
      onChange?.(newValue);
    }
  };

  return (
    <Box className={styles.customPointsRange}>
      <Box className={styles.labelArea}>
        <FormLabel htmlFor="points-range" className={styles.dropdownTitle}>
          {label}
          {isRequired && <span className={styles.requiredAsterisk}>*</span>}
        </FormLabel>
      </Box>
      <StyledTextField
        variant="outlined"
        size="small"
        value={currentValue}
        InputProps={{
          readOnly: true, // Prevent manual typing
          endAdornment: (
            <InputAdornment position="end">
              <Box display="flex" flexDirection="column" alignItems="center">
                <IconButton
                  onClick={handleIncrement}
                  size="small"
                  disabled={currentValue >= max}
                  sx={{ padding: 0 }}
                >
                  <Box style={{ transform: "rotate(180deg)" }}>
                    <KeyboardArrowDown width="20" height="20" />
                  </Box>
                </IconButton>
                <IconButton
                  onClick={handleDecrement}
                  size="small"
                  disabled={currentValue <= min}
                  sx={{ padding: 0 }}
                >
                  <Box>
                    <KeyboardArrowDown width="20" height="20" />
                  </Box>
                </IconButton>
              </Box>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default PointsRangeComponent;
